import { useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import {
  AdminAuditLogsDocument,
  AdminListOrganizationsDocument,
  AdminListUsersDocument,
  AdminUserStatsDocument,
  ManagerAuditLogsDocument,
  ManagerListOrganizationsDocument,
  ManagerListUsersDocument,
  ManagerUserStatsDocument,
} from '../__generated__/graphql';
import {
  adminApi,
  managerApi,
  refetchOrganizations,
  refetchUsers,
  type CreateOrganizationInput,
  type ListAuditLogsParams,
  type ListUsersParams,
  type Organization,
} from '../lib/management.service';
import {
  adminUsersResponseSchema,
  adminUserStatsSchema,
  auditLogsResponseSchema,
  type RoleName,
} from '../lib/admin.schema';
import { useAuthStore } from '@/app/auth/stores/auth.store';

const useAccessToken = () => useAuthStore((s) => s.session?.access_token);
const useIsAdmin = () => useAuthStore((s) => s.hasRole('admin'));

export const useAdminUsers = (params: ListUsersParams = {}) => {
  const accessToken = useAccessToken();
  const isAdmin = useIsAdmin();
  const result = useQuery(isAdmin ? AdminListUsersDocument : ManagerListUsersDocument, {
    variables: params,
    skip: !accessToken,
  });

  const data = useMemo(
    () =>
      result.data?.listUsers ? adminUsersResponseSchema.parse(result.data.listUsers) : undefined,
    [result.data],
  );

  return {
    data,
    isLoading: result.loading,
    isError: Boolean(result.error),
    error: result.error,
  };
};

export const useAdminUserStats = () => {
  const accessToken = useAccessToken();
  const canView = useAuthStore((s) => s.isManagerOrAdmin());
  const isAdmin = useIsAdmin();
  const result = useQuery(isAdmin ? AdminUserStatsDocument : ManagerUserStatsDocument, {
    skip: !accessToken || !canView,
  });

  const data = useMemo(
    () =>
      result.data?.getUserStats ? adminUserStatsSchema.parse(result.data.getUserStats) : undefined,
    [result.data],
  );

  return {
    data,
    isLoading: result.loading,
    isError: Boolean(result.error),
    error: result.error,
  };
};

export const useAdminAuditLogs = (params: ListAuditLogsParams = {}) => {
  const accessToken = useAccessToken();
  const isAdmin = useIsAdmin();
  const canView = useAuthStore((s) => s.hasRole('admin') || s.hasRole('manager'));
  const result = useQuery(isAdmin ? AdminAuditLogsDocument : ManagerAuditLogsDocument, {
    variables: params,
    skip: !accessToken || !canView,
  });

  const data = useMemo(
    () =>
      result.data?.auditLogs ? auditLogsResponseSchema.parse(result.data.auditLogs) : undefined,
    [result.data],
  );

  return {
    data,
    isLoading: result.loading,
    isError: Boolean(result.error),
    error: result.error,
  };
};

export const useAdminOrganizations = () => {
  const accessToken = useAccessToken();
  const isAdmin = useIsAdmin();
  const result = useQuery(
    isAdmin ? AdminListOrganizationsDocument : ManagerListOrganizationsDocument,
    {
      skip: !accessToken,
    },
  );

  const data = useMemo<string[] | undefined>(() => {
    const orgs = result.data?.listOrganizations?.organizations;
    if (!orgs) {
      return undefined;
    }
    return orgs.map((org) => org?.slug).filter((slug): slug is string => slug != null);
  }, [result.data]);

  return {
    data,
    isLoading: result.loading,
    isError: Boolean(result.error),
    error: result.error,
  };
};

export const useAdminOrganizationsFull = () => {
  const accessToken = useAccessToken();
  const isAdmin = useIsAdmin();
  const result = useQuery(
    isAdmin ? AdminListOrganizationsDocument : ManagerListOrganizationsDocument,
    {
      skip: !accessToken,
    },
  );

  const data = useMemo<Organization[] | undefined>(() => {
    const orgs = result.data?.listOrganizations?.organizations;
    if (!orgs) {
      return undefined;
    }
    return orgs.filter((org): org is Organization => org !== null);
  }, [result.data]);

  return {
    data,
    isLoading: result.loading,
    isError: Boolean(result.error),
    error: result.error,
  };
};

const getApi = () => (useAuthStore.getState().hasRole('admin') ? adminApi : managerApi);

export const useSetRole = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (input: { email: string; role: RoleName }) => {
    setIsPending(true);
    setError(null);
    try {
      await getApi().setRole(input.email, input.role);
      await refetchUsers();
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Set role failed');
      setError(err);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutateAsync,
    mutate: (input: { email: string; role: RoleName }) => {
      void mutateAsync(input);
    },
    isPending,
    error,
  };
};

export const useCreateOrganization = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = (input: CreateOrganizationInput) => {
    setIsPending(true);
    setError(null);
    void (async () => {
      try {
        await getApi().createOrganization(input);
        await Promise.all([refetchOrganizations(), refetchUsers()]);
      } catch (e) {
        setError(e instanceof Error ? e : new Error('Create organization failed'));
      } finally {
        setIsPending(false);
      }
    })();
  };

  return { mutate, isPending, error };
};

export const useDeleteOrganization = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (slug: string) => {
    setIsPending(true);
    setError(null);
    try {
      await getApi().deleteOrganization(slug);
      await Promise.all([refetchOrganizations(), refetchUsers()]);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Delete organization failed');
      setError(err);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutateAsync,
    mutate: (slug: string) => {
      void mutateAsync(slug);
    },
    isPending,
    error,
  };
};
